import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { confirm, toast } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useDeleteProductOption, useProductOptionsQuery } from "../api/queries";
import { OptionsListTour, useOptionsListTour } from "../tour";
import { OptionsFilter } from "../ui/OptionsFilter";
import { OptionsTable } from "../ui/OptionsTable";
import { OptionsToolbar } from "../ui/OptionsToolbar";

export function OptionsPage() {
  const nav = useNavigate({ from: "/app/catalog/options" });
  const search: OffsetPageQueryDTO = useSearch({
    from: "/app/catalog/options",
  });
  const list = useProductOptionsQuery({
    offset: search.offset,
    limit: search.limit,
    sort: search.sort,
    filters: search.filters,
  });
  const canManage = useActionPermission(["options:create"]);
  const deleteOption = useDeleteProductOption();
  const { run, handleJoyrideCallback, startTour } = useOptionsListTour();

  const handleDelete = async (id: string) => {
    const ok = await confirm({
      title: "ລຶບ Option",
      description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບ Option ນີ້?",
      actionText: "ລຶບ",
      ActionProps: { variant: "destructive" },
    });
    if (ok) {
      toast.promise(deleteOption.run(id), {
        loading: "ກຳລັງລຶບ...",
        success: "ລຶບ Option ສຳເລັດ",
        error: "ລຶບ Option ລົ້ມເຫຼວ",
      });
    }
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ຕົວເລືອກ" },
          ]}
        />
      </Header>
      <Main>
        <OptionsToolbar
          canManage={!!canManage}
          onCreate={() => nav({ to: "/app/catalog/options/new" })}
          onStartTour={startTour}
        />
        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <OptionsFilter />
          <OptionsTable
            canManage={!!canManage}
            data={list.data?.data ?? []}
            isLoading={list.isLoading}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={list.data?.meta?.total ?? 0}
            sortBy={search.sort?.[0]?.field}
            sortOrder={search.sort?.[0]?.dir as "asc" | "desc" | undefined}
            onPaginationChange={(offset, limit) =>
              nav({ search: { ...search, offset, limit } })
            }
            onSortingChange={(id, desc) =>
              nav({
                search: {
                  ...search,
                  sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                },
              })
            }
            onEdit={(row) =>
              nav({
                to: "/app/catalog/options/$id/edit",
                params: { id: row.id },
              })
            }
            onDelete={handleDelete}
          />
        </div>
        <OptionsListTour run={run} onCallback={handleJoyrideCallback} />
      </Main>
    </>
  );
}
